import { CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { FormPhase } from './usePracticeDetailsForm';

interface SaveBarProps {
    phase: FormPhase;
    dirtyCount: number;
    dirtyLabels: string[];
    onSave: () => void;
    onDiscard: () => void;
}

interface DirtyContentProps {
    dirtyCount: number;
    dirtyLabels: string[];
    saving: boolean;
    onSave: () => void;
    onDiscard: () => void;
}

function DirtyContent({ dirtyCount, dirtyLabels, saving, onSave, onDiscard }: DirtyContentProps) {
    return (
        <>
            <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-2 h-2 rounded-full bg-[#FECA42] shrink-0" />
                <span className="text-[13px] font-semibold text-white whitespace-nowrap">
                    {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
                </span>
                <span className="text-[13px] text-gray-400 truncate">
                    {dirtyLabels.join(' · ')}
                </span>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
                <button
                    type="button"
                    onClick={onDiscard}
                    disabled={saving}
                    className="text-[13px] font-semibold text-gray-300 hover:text-white px-3 h-9 disabled:opacity-50"
                >
                    Discard
                </button>
                <button
                    type="button"
                    onClick={onSave}
                    disabled={saving}
                    className="bg-white text-gray-900 text-[13px] font-semibold px-4 h-9 rounded-lg hover:bg-gray-100 disabled:opacity-60 flex items-center gap-2"
                >
                    {saving && (
                        <span className="h-3.5 w-3.5 rounded-full border-2 border-gray-400 border-t-gray-900 animate-spin shrink-0" />
                    )}
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
            </div>
        </>
    );
}

interface ErrorContentProps {
    dirtyCount: number;
    onSave: () => void;
    onDiscard: () => void;
}

function ErrorContent({ dirtyCount, onDiscard, onSave }: ErrorContentProps) {
    return (
        <>
            <div className="flex items-center gap-2.5 min-w-0">
                <ExclamationTriangleIcon className="h-4 w-4 text-red-600 shrink-0" />
                <span className="text-[13px] font-semibold text-gray-900 whitespace-nowrap">
                    {dirtyCount} change{dirtyCount === 1 ? '' : 's'} not saved
                </span>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
                <button
                    type="button"
                    onClick={onDiscard}
                    className="text-[13px] font-semibold text-gray-500 hover:text-gray-900 px-3 h-9"
                >
                    Discard all
                </button>
                <button
                    type="button"
                    onClick={onSave}
                    className="bg-gray-900 text-white font-semibold px-4 h-9 rounded-lg text-[13px] hover:bg-black flex items-center gap-2"
                >
                    <CheckIcon className="h-4 w-4" />
                    Save again
                </button>
            </div>
        </>
    );
}

// Persistent save bar - lives at the top of the content column (see
// DetailsTab), above whichever pane is active, so dirty state stays visible
// from Info/Account/Files/Beta alike (see D2SaveBar in the design source).
// No command-S badge and no "Review" link: this build drops keyboard-save
// affordances and the extra jump-to-field shortcut to match the mock's
// dirty state exactly (ErrorBanner's "Fix X" already covers the jump-to-field
// need for a failed save).
export function SaveBar({ phase, dirtyCount, dirtyLabels, onSave, onDiscard }: SaveBarProps) {
    const isError = phase === 'error';
    const isSaving = phase === 'saving';

    // Nothing to say while the form is clean - the rail's Complete block
    // carries the "last edited" fact instead (see DetailNav).
    if (phase === 'idle' || phase === 'saved') return null;

    const containerClass = isError
        ? 'bg-white border-red-200 shadow-[0px_1px_2px_rgba(0,0,0,0.06)]'
        : 'bg-gray-900 border-gray-900 shadow-[0_8px_24px_rgba(0,0,0,0.16)]';

    return (
        <div
            className={`h-14 shrink-0 px-5 rounded-xl border flex items-center justify-between gap-4 ${containerClass}`}
        >
            {(phase === 'dirty' || isSaving) && (
                <DirtyContent
                    dirtyCount={dirtyCount}
                    dirtyLabels={dirtyLabels}
                    saving={isSaving}
                    onSave={onSave}
                    onDiscard={onDiscard}
                />
            )}
            {isError && (
                <ErrorContent dirtyCount={dirtyCount} onSave={onSave} onDiscard={onDiscard} />
            )}
        </div>
    );
}
